import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MessagesTab from "@/components/trainer/MessagesTab";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  LogOut,
  Users,
  Dog,
  Calendar,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  FileText,
  Camera,
  GraduationCap,
  Plus,
  Truck,
  BookOpen,
  Bell,
  Video,
  UserPlus,
  ExternalLink,
  Mail,
  Trash2,
  Pencil,
  Eye,
  MapPin,
  Phone,
  Printer,
  Check,
  Archive,
  ArchiveRestore
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ResourcesTab } from "@/components/trainer/ResourcesTab";
import { CertificateEditorDialog } from "@/components/trainer/CertificateEditor";
import { IntakesTab } from "@/components/trainer/IntakesTab";
import { DogsTab } from "@/components/trainer/DogsTab";
import { WaitlistTab } from "@/components/trainer/WaitlistTab";
import { BreedersTab } from "@/components/trainer/BreedersTab";
import { InvoicesTab } from "@/components/trainer/InvoicesTab";

import { printElementById } from "@/utils/printUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOMEWORK_FOCUS_AREAS = [
  { value: "engagement", label: "Engagement" },
  { value: "recall", label: "Recall" },
  { value: "lead_walking", label: "Lead Walking" },
  { value: "impulse_control", label: "Impulse Control" },
  { value: "settling", label: "Settling" },
  { value: "socialisation", label: "Socialisation" }
];

const TrainerDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({});
  const [todaySchedule, setTodaySchedule] = useState({ puppies: [], total_puppies: 0 });
  const [clients, setClients] = useState([]);
  const [puppies, setPuppies] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [breederReferrals, setBreederReferrals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Photo upload state
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [selectedPuppy, setSelectedPuppy] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Modal states
  const [messageOpen, setMessageOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [messageContent, setMessageContent] = useState("");
  
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [reportData, setReportData] = useState({
    week_number: 1,
    session_date: new Date().toISOString().split("T")[0],
    topics_covered: [], // [{topic_id, topic_name, comment}]
    trainer_comments: "",
    highlights: "",
    areas_to_focus: ""
  });

  // New topic dialog
  const [newTopicOpen, setNewTopicOpen] = useState(false);
  const [newTopicData, setNewTopicData] = useState({
    name: "",
    description: "",
    program_type: "puppy_foundations",
    week_number: 1
  });

  // Client video upload
  const [clientVideoOpen, setClientVideoOpen] = useState(false);
  const [clientVideoData, setClientVideoData] = useState({
    enrollment_id: "",
    title: "",
    description: "",
    week_number: 1
  });
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [clientVideos, setClientVideos] = useState([]);
  const [archivedVideos, setArchivedVideos] = useState([]);
  const [showArchivedVideos, setShowArchivedVideos] = useState(false);
  const [videoStorageStats, setVideoStorageStats] = useState(null);

  const [homeworkOpen, setHomeworkOpen] = useState(false);
  const [homeworkData, setHomeworkData] = useState({
    week_number: 1,
    title: "",
    focus_area: "",
    exercises: [""],
    tips: "",
    duration_minutes: 5
  });

  const [activeTab, setActiveTab] = useState("enrollments");

  // Manual enrollment states
  const [manualEnrollOpen, setManualEnrollOpen] = useState(false);
  const [manualEnrollData, setManualEnrollData] = useState({
    client_id: "",
    puppy_id: "",
    intake_id: "",
    notes: ""
  });
  const [selectedClientPuppies, setSelectedClientPuppies] = useState([]);

  // Training Resources states
  const [trainingResources, setTrainingResources] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);

  // Client detail view state
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);

  // Certificate editor states
  const [certificateOpen, setCertificateOpen] = useState(false);
  const [selectedCertificateEnrollment, setSelectedCertificateEnrollment] = useState(null);

  // Memoized computed values to prevent recalculation on every render
  const activeEnrollments = useMemo(() => 
    enrollments.filter(e => ["confirmed", "in_progress"].includes(e.status)),
    [enrollments]
  );

  const availableIntakes = useMemo(() => 
    intakes.filter(i => i.status === "upcoming" && i.available_spots > 0),
    [intakes]
  );

  // Helper to get client's enrollments

  // Helper to get client's puppies and enrollments
  const getClientPuppies = useCallback((clientId) => {
    return puppies.filter(p => p.user_id === clientId);
  }, [puppies]);

  const getClientEnrollments = useCallback((clientId) => {
    return enrollments.filter(e => e.user_id === clientId);
  }, [enrollments]);

  // Helper to get intake attendees
  const getIntakeAttendees = useCallback((intakeId) => {
    return enrollments.filter(e => e.intake_id === intakeId && e.status !== "cancelled");
  }, [enrollments]);

  // Print function - uses safe DOM manipulation instead of document.write
  const handlePrint = (elementId, title) => {
    printElementById(elementId, title);
  };

  const fetchAllData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, todayRes, clientsRes, puppiesRes, enrollmentsRes, intakesRes, waitlistRes, breederRes, resourcesRes, templatesRes, topicsRes] = await Promise.all([
        axios.get(`${API}/trainer/stats`, { headers }),
        axios.get(`${API}/trainer/today`, { headers }),
        axios.get(`${API}/trainer/clients`, { headers }),
        axios.get(`${API}/puppies`, { headers }),
        axios.get(`${API}/enrollments`, { headers }),
        axios.get(`${API}/intakes?include_full=true`),
        axios.get(`${API}/waitlist`, { headers }),
        axios.get(`${API}/breeder-referrals`, { headers }),
        axios.get(`${API}/training-resources`, { headers }),
        axios.get(`${API}/email-templates`, { headers }),
        axios.get(`${API}/training-topics`, { headers })
      ]);
      
      setStats(statsRes.data);
      setTodaySchedule(todayRes.data);
      setClients(clientsRes.data);
      setPuppies(puppiesRes.data);
      setEnrollments(enrollmentsRes.data);
      setIntakes(intakesRes.data);
      setWaitlist(waitlistRes.data);
      setBreederReferrals(breederRes.data);
      setTrainingResources(resourcesRes.data);
      setEmailTemplates(templatesRes.data);
      setTrainingTopics(topicsRes.data);
      
      // Fetch client videos for all enrollments
      try {
        const allVideos = [];
        for (const enrollment of enrollmentsRes.data.filter(e => ["confirmed", "in_progress"].includes(e.status))) {
          const videosRes = await axios.get(`${API}/client-videos/enrollment/${enrollment.id}`, { headers });
          allVideos.push(...videosRes.data);
        }
        setVideos(allVideos);
        
        // Also fetch archived videos
        try {
          const archivedRes = await axios.get(`${API}/client-videos/archived`, { headers });
          setArchivedVideos(archivedRes.data || []);
        } catch (archiveErr) {
          console.debug("Could not fetch archived videos:", archiveErr?.message);
          setArchivedVideos([]);
        }
        
        // Fetch storage stats
        try {
          const statsRes = await axios.get(`${API}/client-videos/storage-stats`, { headers });
          setVideoStorageStats(statsRes.data);
        } catch (statsErr) {
          console.debug("Could not fetch storage stats:", statsErr?.message);
        }
      } catch (videoErr) {
        console.debug("Could not fetch client videos:", videoErr?.message);
        setVideos([]);
      }
    } catch (err) {
      toast.error("Failed to load trainer data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleStatusUpdate = async (type, id, newStatus) => {
    try {
      const headers = getAuthHeaders();
      if (type === "enrollment") {
        await axios.put(`${API}/enrollments/${id}/status?status=${newStatus}`, {}, { headers });
      } else if (type === "intake") {
        await axios.put(`${API}/intakes/${id}/status?status=${newStatus}`, {}, { headers });
      } else if (type === "waitlist") {
        await axios.put(`${API}/waitlist/${id}/status?status=${newStatus}`, {}, { headers });
      }
      toast.success("Status updated successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSendMessage = async () => {
    if (!selectedRecipient || !messageContent.trim()) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/messages`, {
        recipient_id: selectedRecipient.id,
        content: messageContent.trim()
      }, { headers });
      
      toast.success(`Message sent to ${selectedRecipient.name}`);
      setMessageOpen(false);
      setMessageContent("");
      setSelectedRecipient(null);
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleCreateReportCard = async () => {
    if (!selectedEnrollment) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/report-cards`, {
        enrollment_id: selectedEnrollment.id,
        ...reportData
      }, { headers });
      
      toast.success(`Report card created for ${selectedEnrollment.puppy_name}`);
      setReportOpen(false);
      setReportData({
        week_number: 1,
        session_date: new Date().toISOString().split("T")[0],
        topics_covered: [],
        trainer_comments: "",
        highlights: "",
        areas_to_focus: ""
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create report card");
    }
  };

  // Add new training topic
  const handleAddTopic = async () => {
    if (!newTopicData.name.trim()) {
      toast.error("Topic name is required");
      return;
    }
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/training-topics`, newTopicData, { headers });
      toast.success("Topic added!");
      setNewTopicOpen(false);
      setNewTopicData({ name: "", description: "", program_type: "puppy_foundations", week_number: 1 });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add topic");
    }
  };

  // Delete training topic
  const handleDeleteTopic = async (topicId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/training-topics/${topicId}`, { headers });
      toast.success("Topic deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete topic");
    }
  };

  // Video upload progress state
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  // Upload client video - direct to S3 using presigned URL
  const handleUploadClientVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !clientVideoData.enrollment_id) return;
    
    setUploadingVideo(true);
    setVideoUploadProgress(0);
    
    const headers = getAuthHeaders();
    
    try {
      // Step 1: Get presigned URL from backend
      const initRes = await axios.post(`${API}/client-videos/presigned-upload`, {
        enrollment_id: clientVideoData.enrollment_id,
        title: clientVideoData.title || file.name,
        week_number: clientVideoData.week_number,
        description: clientVideoData.description || "",
        filename: file.name,
        content_type: file.type || "video/mp4",
        file_size: file.size
      }, { headers });
      
      const { video_id, upload_url, s3_key } = initRes.data;
      
      // Step 2: Upload directly to S3 using presigned URL
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setVideoUploadProgress(progress);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error - check your connection and try again'));
        });
        
        xhr.open('PUT', upload_url);
        xhr.send(file);
      });
      
      // Step 3: Notify backend that upload is complete
      await axios.post(`${API}/client-videos/confirm-upload`, {
        video_id: video_id,
        s3_key: s3_key
      }, { headers });
      
      toast.success("Video uploaded successfully!", { duration: 5000 });
      setClientVideoOpen(false);
      setClientVideoData({ enrollment_id: "", title: "", description: "", week_number: 1 });
      setVideoUploadProgress(0);
      fetchAllData();
    } catch (error) {
      console.error("Video upload error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to upload video";
      toast.error(errorMsg, { duration: 10000 });
      setVideoUploadProgress(0);
    } finally {
      setUploadingVideo(false);
    }
  };

  // Delete client video
  const handleDeleteClientVideo = async (videoId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/client-videos/${videoId}`, { headers });
      toast.success("Video deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  // Archive/unarchive video
  const handleArchiveVideo = async (videoId, archive = true) => {
    try {
      const headers = getAuthHeaders();
      const endpoint = archive ? "archive" : "unarchive";
      await axios.put(`${API}/client-videos/${videoId}/${endpoint}`, {}, { headers });
      toast.success(archive ? "Video archived" : "Video restored");
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to ${archive ? "archive" : "restore"} video`);
    }
  };

  // Archive all videos for an enrollment
  const handleArchiveEnrollmentVideos = async (enrollmentId, puppyName) => {
    if (!window.confirm(`Archive all videos for ${puppyName}? They will still be accessible in the Archived Videos section.`)) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.put(`${API}/client-videos/enrollment/${enrollmentId}/archive-all`, {}, { headers });
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to archive videos");
    }
  };

  // Get topics for a specific week and program
  const getTopicsForWeek = (weekNumber, programType) => {
    return trainingTopics.filter(t => t.week_number === weekNumber && t.program_type === programType);
  };

  // Toggle topic in report card
  const toggleTopicInReport = (topic) => {
    setReportData(prev => {
      const exists = prev.topics_covered.find(t => t.topic_id === topic.id);
      if (exists) {
        return { ...prev, topics_covered: prev.topics_covered.filter(t => t.topic_id !== topic.id) };
      } else {
        return { 
          ...prev, 
          topics_covered: [...prev.topics_covered, { topic_id: topic.id, topic_name: topic.name, comment: "" }]
        };
      }
    });
  };

  // Update topic comment in report
  const updateTopicComment = (topicId, comment) => {
    setReportData(prev => ({
      ...prev,
      topics_covered: prev.topics_covered.map(t => 
        t.topic_id === topicId ? { ...t, comment } : t
      )
    }));
  };

  const handleCreateHomework = async () => {
    if (!selectedEnrollment) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/homework`, {
        enrollment_id: selectedEnrollment.id,
        ...homeworkData,
        exercises: homeworkData.exercises.filter(e => e.trim())
      }, { headers });
      
      toast.success(`Homework assigned to ${selectedEnrollment.puppy_name}`);
      setHomeworkOpen(false);
      setHomeworkData({ week_number: 1, title: "", focus_area: "", exercises: [""], tips: "", duration_minutes: 5 });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create homework");
    }
  };

  const handleGraduate = async (enrollmentId) => {
    // Find the enrollment to pass to certificate editor
    const enrollment = enrollments.find(e => e.id === enrollmentId);
    if (!enrollment) {
      toast.error("Enrollment not found");
      return;
    }
    
    // Get owner info
    const owner = clients.find(c => c.id === enrollment.user_id);
    
    setSelectedCertificateEnrollment({
      ...enrollment,
      owner_name: owner?.name || ""
    });
    setCertificateOpen(true);
  };

  const handleCertificateCreated = () => {
    fetchAllData();
  };

  const handleSendBreederInvitation = async (referralId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/breeder-referrals/${referralId}/send-invitation`, {}, { headers });
      toast.success("Invitation sent!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to send invitation");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPuppy) return;
    
    setUploadingPhoto(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/puppies/${selectedPuppy.id}/photos`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Photo added for ${selectedPuppy.name}!`);
      setPhotoUploadOpen(false);
      setSelectedPuppy(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Manual enrollment handlers
  const handleClientSelect = (clientId) => {
    setManualEnrollData(prev => ({ ...prev, client_id: clientId, puppy_id: "" }));
    const clientPuppies = puppies.filter(p => p.user_id === clientId);
    setSelectedClientPuppies(clientPuppies);
  };

  const handleManualEnroll = async () => {
    if (!manualEnrollData.puppy_id || !manualEnrollData.intake_id) {
      toast.error("Please select a puppy and intake");
      return;
    }
    
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/trainer/enrollments`, {
        puppy_id: manualEnrollData.puppy_id,
        intake_id: manualEnrollData.intake_id,
        notes: manualEnrollData.notes
      }, { headers });
      
      toast.success("Enrollment created successfully!");
      setManualEnrollOpen(false);
      setManualEnrollData({ client_id: "", puppy_id: "", intake_id: "", notes: "" });
      setSelectedClientPuppies([]);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create enrollment");
    }
  };

  const handleSendPaymentLink = async (enrollmentId, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/trainer/enrollments/${enrollmentId}/send-payment-link`, {}, { headers });
      toast.success(`Payment link sent for ${puppyName}!`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payment link");
    }
  };

  // Delete handlers
  const handleDeleteClient = async (clientId, clientName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/clients/${clientId}`, { headers });
      toast.success(`${clientName} and all associated data deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  const handleDeletePuppy = async (puppyId, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/puppies/${puppyId}`, { headers });
      toast.success(`${puppyName} and all associated data deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete puppy");
    }
  };

  const handleDeleteEnrollment = async (enrollmentId, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/enrollments/${enrollmentId}`, { headers });
      toast.success(`Enrollment for ${puppyName} deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete enrollment");
    }
  };

  const handleDeleteIntake = async (intakeId, programName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/intakes/${intakeId}`, { headers });
      toast.success(`${programName} intake deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete intake");
    }
  };

  const handleDeleteWaitlist = async (waitlistId, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/waitlist/${waitlistId}`, { headers });
      toast.success(`${puppyName} removed from waitlist`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from waitlist");
    }
  };

  const handleDeleteBreederReferral = async (referralId, ownerName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/breeder-referrals/${referralId}`, { headers });
      toast.success(`Referral for ${ownerName} deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete referral");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "bg-olive text-white";
      case "in_progress":
        return "bg-olive/80 text-white";
      case "pending_payment":
      case "upcoming":
        return "bg-terracotta text-white";
      case "cancelled":
        return "bg-stone text-white";
      default:
        return "bg-sand text-charcoal";
    }
  };

  const programDays = {
    puppy_foundations: ["Thursday"],
    puppy_development: ["Friday"]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="trainer-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex flex-col">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.print()}
              className="hidden md:flex"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Page
            </Button>
            <Badge className="bg-olive">Trainer</Badge>
            <span className="text-charcoal font-medium hidden md:inline">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-sand rounded-full transition-colors"
              data-testid="trainer-logout"
            >
              <LogOut className="w-5 h-5 text-charcoal" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-heading text-3xl text-charcoal mb-8">Trainer Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("clients")}
            data-testid="stat-clients"
          >
            <Users className="w-6 h-6 text-olive mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.total_clients || 0}</p>
            <p className="text-stone text-xs">Clients</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("puppies")}
            data-testid="stat-dogs"
          >
            <Dog className="w-6 h-6 text-olive mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.total_puppies || 0}</p>
            <p className="text-stone text-xs">Dogs</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("enrollments")}
            data-testid="stat-active"
          >
            <CheckCircle className="w-6 h-6 text-olive mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.active_enrollments || 0}</p>
            <p className="text-stone text-xs">Active</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("enrollments")}
            data-testid="stat-pending"
          >
            <Clock className="w-6 h-6 text-terracotta mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.pending_enrollments || 0}</p>
            <p className="text-stone text-xs">Pending</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("intakes")}
            data-testid="stat-intakes"
          >
            <Calendar className="w-6 h-6 text-olive mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.upcoming_intakes || 0}</p>
            <p className="text-stone text-xs">Intakes</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("waitlist")}
            data-testid="stat-waitlist"
          >
            <Bell className="w-6 h-6 text-terracotta mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.waitlist_count || 0}</p>
            <p className="text-stone text-xs">Waitlist</p>
          </div>
          <div 
            className="card-premium text-center cursor-pointer hover:border-olive/30 transition-colors"
            onClick={() => setActiveTab("enrollments")}
            data-testid="stat-graduated"
          >
            <GraduationCap className="w-6 h-6 text-olive mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-heading text-2xl text-charcoal">{stats.graduations_count || 0}</p>
            <p className="text-stone text-xs">Graduated</p>
          </div>
        </div>

        {/* Today's Schedule */}
        {todaySchedule.total_puppies > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium mb-8 bg-olive/5 border-olive/20"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-2xl text-charcoal">Today's Schedule</h2>
                <p className="text-stone">{todaySchedule.day}, {todaySchedule.date}</p>
              </div>
              <Badge className="bg-olive text-lg px-4 py-2">
                {todaySchedule.total_puppies} {todaySchedule.total_puppies === 1 ? "puppy" : "puppies"}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todaySchedule.puppies.map((puppy) => (
                <div key={puppy.enrollment_id || puppy.puppy_name} className="bg-white p-4 rounded-lg border border-olive/10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-charcoal">{puppy.puppy_name}</h3>
                    <Badge className="bg-sand text-charcoal text-xs">Week {puppy.session_number}</Badge>
                  </div>
                  <p className="text-stone text-sm">{puppy.breed}</p>
                  <p className="text-stone text-sm">{puppy.program_name}</p>
                  <div className="mt-2 pt-2 border-t border-olive/10">
                    <p className="text-xs text-stone flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      {puppy.pickup_address}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand flex-wrap">
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="intakes" data-testid="tab-intakes">Intakes</TabsTrigger>
            <TabsTrigger value="puppies" data-testid="tab-puppies">Dogs</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="breeders" data-testid="tab-breeders">Breeders</TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources">Resources</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
          </TabsList>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments">
            <div className="flex justify-end mb-4">
              <Button 
                className="btn-primary"
                onClick={() => setManualEnrollOpen(true)}
                data-testid="manual-enroll-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manual Enroll
              </Button>
            </div>
            <div className="card-premium p-0 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-sand/50">
                    <TableHead>Puppy</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Referral</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-stone">
                        No enrollments yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    enrollments.map((enrollment) => (
                      <TableRow key={enrollment.id} data-testid={`enrollment-row-${enrollment.id}`}>
                        <TableCell className="font-medium">
                          {enrollment.puppy_name}
                        </TableCell>
                        <TableCell>{enrollment.program_name}</TableCell>
                        <TableCell>{enrollment.start_date}</TableCell>
                        <TableCell>{enrollment.day_of_week}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{enrollment.sessions_completed}/4</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(enrollment.status)}>
                            {enrollment.status.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-stone">
                            {enrollment.referral_source ? enrollment.referral_source.replace("_", " ") : "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 flex-wrap">
                            {/* Send Payment Link for pending payment enrollments */}
                            {(enrollment.status === "pending_payment" || enrollment.status === "pending") && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-olive border-olive hover:bg-olive hover:text-white"
                                  onClick={() => handleSendPaymentLink(enrollment.id, enrollment.puppy_name)}
                                  data-testid={`send-payment-${enrollment.id}`}
                                >
                                  <Mail className="w-3 h-3 mr-1" />
                                  Send Payment Link
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                                  onClick={async () => {
                                    if (window.confirm(`Mark ${enrollment.puppy_name}'s enrollment as PAID/CONFIRMED? Only do this if you've verified payment was received.`)) {
                                      try {
                                        await axios.put(`${API}/enrollments/${enrollment.id}/status?status=confirmed`, {}, { headers: getAuthHeaders() });
                                        toast.success(`${enrollment.puppy_name}'s enrollment marked as confirmed!`);
                                        fetchAllData();
                                      } catch (err) {
                                        toast.error("Failed to update status");
                                      }
                                    }
                                  }}
                                  data-testid={`mark-paid-${enrollment.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1" />
                                  Mark as Paid
                                </Button>
                              </>
                            )}
                            {/* Resend Payment Link for any enrollment (for testing) */}
                            {enrollment.status !== "pending_payment" && enrollment.status !== "pending" && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-stone hover:text-olive"
                                onClick={() => handleSendPaymentLink(enrollment.id, enrollment.puppy_name)}
                                data-testid={`resend-payment-${enrollment.id}`}
                              >
                                <Mail className="w-3 h-3 mr-1" />
                                Resend Link
                              </Button>
                            )}
                            {["confirmed", "in_progress"].includes(enrollment.status) && (
                              <>
                                <Dialog open={reportOpen && selectedEnrollment?.id === enrollment.id} onOpenChange={(open) => {
                                  setReportOpen(open);
                                  if (open) {
                                    setSelectedEnrollment(enrollment);
                                    const weekNum = Math.min(enrollment.sessions_completed + 1, 4);
                                    // Pre-populate topics for this week
                                    const weekTopics = getTopicsForWeek(weekNum, enrollment.program_type);
                                    setReportData(prev => ({ 
                                      ...prev, 
                                      week_number: weekNum,
                                      topics_covered: weekTopics.map(t => ({ topic_id: t.id, topic_name: t.name, comment: "" }))
                                    }));
                                  }
                                }}>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" data-testid={`report-${enrollment.id}`}>
                                      <FileText className="w-3 h-3 mr-1" />
                                      Report
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                      <DialogTitle>Report Card for {enrollment.puppy_name}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Week Number</Label>
                                          <Select
                                            value={reportData.week_number.toString()}
                                            onValueChange={(v) => {
                                              const weekNum = parseInt(v);
                                              const weekTopics = getTopicsForWeek(weekNum, enrollment.program_type);
                                              setReportData(prev => ({ 
                                                ...prev, 
                                                week_number: weekNum,
                                                topics_covered: weekTopics.map(t => ({ topic_id: t.id, topic_name: t.name, comment: "" }))
                                              }));
                                            }}
                                          >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {[1, 2, 3, 4].map(n => (
                                                <SelectItem key={n} value={n.toString()}>Week {n}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Session Date</Label>
                                          <Input
                                            type="date"
                                            value={reportData.session_date}
                                            onChange={(e) => setReportData(prev => ({ ...prev, session_date: e.target.value }))}
                                          />
                                        </div>
                                      </div>
                                      
                                      {/* Topics Covered This Week */}
                                      <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                          <Label className="text-base font-semibold">Topics Covered</Label>
                                          <Button size="sm" variant="outline" onClick={() => setNewTopicOpen(true)}>
                                            <Plus className="w-3 h-3 mr-1" /> Add Topic
                                          </Button>
                                        </div>
                                        
                                        {/* Available topics for this week */}
                                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                          {getTopicsForWeek(reportData.week_number, enrollment.program_type).length === 0 ? (
                                            <p className="text-stone text-sm italic">No topics defined for Week {reportData.week_number}. Click "Add Topic" to create some.</p>
                                          ) : (
                                            getTopicsForWeek(reportData.week_number, enrollment.program_type).map(topic => {
                                              const isSelected = reportData.topics_covered.some(t => t.topic_id === topic.id);
                                              const topicData = reportData.topics_covered.find(t => t.topic_id === topic.id);
                                              return (
                                                <div key={topic.id} className={`p-3 rounded-lg border ${isSelected ? 'border-olive bg-olive/10' : 'border-sand'}`}>
                                                  <div className="flex items-start gap-3">
                                                    <input
                                                      type="checkbox"
                                                      checked={isSelected}
                                                      onChange={() => toggleTopicInReport(topic)}
                                                      className="mt-1 w-4 h-4 text-olive rounded"
                                                    />
                                                    <div className="flex-1">
                                                      <div className="flex justify-between">
                                                        <span className="font-medium text-charcoal">{topic.name}</span>
                                                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDeleteTopic(topic.id)}>
                                                          <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                      </div>
                                                      {topic.description && <p className="text-xs text-stone">{topic.description}</p>}
                                                      {isSelected && (
                                                        <Input
                                                          className="mt-2"
                                                          placeholder="Add comment for this topic..."
                                                          value={topicData?.comment || ""}
                                                          onChange={(e) => updateTopicComment(topic.id, e.target.value)}
                                                        />
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      </div>

                                      <div className="space-y-2">
                                        <Label>Trainer Comments</Label>
                                        <Textarea
                                          value={reportData.trainer_comments}
                                          onChange={(e) => setReportData(prev => ({ ...prev, trainer_comments: e.target.value }))}
                                          rows={3}
                                          placeholder="Overall notes about the session..."
                                        />
                                      </div>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Highlights</Label>
                                          <Input
                                            value={reportData.highlights}
                                            onChange={(e) => setReportData(prev => ({ ...prev, highlights: e.target.value }))}
                                            placeholder="What went well..."
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Areas to Focus</Label>
                                          <Input
                                            value={reportData.areas_to_focus}
                                            onChange={(e) => setReportData(prev => ({ ...prev, areas_to_focus: e.target.value }))}
                                            placeholder="Needs more work on..."
                                          />
                                        </div>
                                      </div>
                                      <Button onClick={handleCreateReportCard} className="btn-primary w-full">
                                        Save Report Card
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>

                                {/* Add Topic Dialog */}
                                <Dialog open={newTopicOpen} onOpenChange={setNewTopicOpen}>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Add Training Topic</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                      <div className="space-y-2">
                                        <Label>Topic Name *</Label>
                                        <Input
                                          value={newTopicData.name}
                                          onChange={(e) => setNewTopicData(prev => ({ ...prev, name: e.target.value }))}
                                          placeholder="e.g., Loose Leash Walking"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Description (Optional)</Label>
                                        <Input
                                          value={newTopicData.description}
                                          onChange={(e) => setNewTopicData(prev => ({ ...prev, description: e.target.value }))}
                                          placeholder="Brief description..."
                                        />
                                      </div>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Program</Label>
                                          <Select
                                            value={newTopicData.program_type}
                                            onValueChange={(v) => setNewTopicData(prev => ({ ...prev, program_type: v }))}
                                          >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                                              <SelectItem value="puppy_development">Puppy Development</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Week</Label>
                                          <Select
                                            value={newTopicData.week_number.toString()}
                                            onValueChange={(v) => setNewTopicData(prev => ({ ...prev, week_number: parseInt(v) }))}
                                          >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {[1, 2, 3, 4].map(n => (
                                                <SelectItem key={n} value={n.toString()}>Week {n}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                      <Button onClick={handleAddTopic} className="btn-primary w-full">Add Topic</Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>

                                {/* Upload Video Button */}
                                <Dialog open={clientVideoOpen && selectedEnrollment?.id === enrollment.id} onOpenChange={(open) => {
                                  setClientVideoOpen(open);
                                  if (open) {
                                    setSelectedEnrollment(enrollment);
                                    setClientVideoData(prev => ({ 
                                      ...prev, 
                                      enrollment_id: enrollment.id,
                                      week_number: Math.min(enrollment.sessions_completed + 1, 4)
                                    }));
                                  }
                                }}>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" data-testid={`video-${enrollment.id}`}>
                                      <Video className="w-3 h-3 mr-1" />
                                      Video
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Upload Video for {enrollment.puppy_name}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                      <div className="space-y-2">
                                        <Label>Video Title</Label>
                                        <Input
                                          value={clientVideoData.title}
                                          onChange={(e) => setClientVideoData(prev => ({ ...prev, title: e.target.value }))}
                                          placeholder="e.g., Week 1 Recall Practice"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Week Number</Label>
                                        <Select
                                          value={clientVideoData.week_number.toString()}
                                          onValueChange={(v) => setClientVideoData(prev => ({ ...prev, week_number: parseInt(v) }))}
                                        >
                                          <SelectTrigger><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {[1, 2, 3, 4].map(n => (
                                              <SelectItem key={n} value={n.toString()}>Week {n}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Description (Optional)</Label>
                                        <Textarea
                                          value={clientVideoData.description}
                                          onChange={(e) => setClientVideoData(prev => ({ ...prev, description: e.target.value }))}
                                          placeholder="Notes about this video..."
                                          rows={2}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Select Video File</Label>
                                        <input
                                          type="file"
                                          accept="video/*"
                                          onChange={handleUploadClientVideo}
                                          disabled={uploadingVideo}
                                          className="block w-full text-sm text-stone file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-olive file:text-white hover:file:bg-olive/90 disabled:opacity-50"
                                        />
                                        <p className="text-xs text-stone">Supports large files up to 1GB. MP4, MOV, AVI, WebM</p>
                                      </div>
                                      {uploadingVideo && (
                                        <div className="py-4">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-stone text-sm">
                                              {videoUploadProgress < 100 ? "Uploading video..." : "Processing video..."}
                                            </span>
                                            <span className="text-olive font-medium">{videoUploadProgress}%</span>
                                          </div>
                                          <div className="w-full bg-sand rounded-full h-3">
                                            <div 
                                              className={`h-3 rounded-full transition-all duration-300 ${videoUploadProgress >= 100 ? 'bg-terracotta animate-pulse' : 'bg-olive'}`}
                                              style={{ width: `${videoUploadProgress}%` }}
                                            />
                                          </div>
                                          {videoUploadProgress < 100 ? (
                                            <p className="text-xs text-stone mt-2">Please don't close this window while uploading...</p>
                                          ) : (
                                            <p className="text-xs text-terracotta mt-2 font-medium">
                                              ⏳ Processing large file... This may take a few minutes. Check the toast for status.
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </DialogContent>
                                </Dialog>

                                <Dialog open={homeworkOpen && selectedEnrollment?.id === enrollment.id} onOpenChange={(open) => {
                                  setHomeworkOpen(open);
                                  if (open) {
                                    setSelectedEnrollment(enrollment);
                                    setHomeworkData(prev => ({ ...prev, week_number: enrollment.sessions_completed + 1 }));
                                  }
                                }}>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" data-testid={`homework-${enrollment.id}`}>
                                      <BookOpen className="w-3 h-3 mr-1" />
                                      HW
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Homework for {enrollment.puppy_name}</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Week</Label>
                                          <Select
                                            value={homeworkData.week_number.toString()}
                                            onValueChange={(v) => setHomeworkData(prev => ({ ...prev, week_number: parseInt(v) }))}
                                          >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {[1, 2, 3, 4].map(n => (
                                                <SelectItem key={n} value={n.toString()}>Week {n}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Focus Area</Label>
                                          <Select
                                            value={homeworkData.focus_area}
                                            onValueChange={(v) => setHomeworkData(prev => ({ ...prev, focus_area: v }))}
                                          >
                                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                              {HOMEWORK_FOCUS_AREAS.map(area => (
                                                <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Title</Label>
                                        <Input
                                          value={homeworkData.title}
                                          onChange={(e) => setHomeworkData(prev => ({ ...prev, title: e.target.value }))}
                                          placeholder="e.g., Recall Practice"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Daily Duration (minutes)</Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="30"
                                          value={homeworkData.duration_minutes}
                                          onChange={(e) => setHomeworkData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 5 }))}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Exercises</Label>
                                        {homeworkData.exercises.map((ex, i) => (
                                          <Input
                                            key={`exercise-input-${i}`}
                                            value={ex}
                                            onChange={(e) => {
                                              const newExercises = [...homeworkData.exercises];
                                              newExercises[i] = e.target.value;
                                              setHomeworkData(prev => ({ ...prev, exercises: newExercises }));
                                            }}
                                            placeholder={`Exercise ${i + 1}`}
                                          />
                                        ))}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setHomeworkData(prev => ({ ...prev, exercises: [...prev.exercises, ""] }))}
                                        >
                                          <Plus className="w-3 h-3 mr-1" /> Add Exercise
                                        </Button>
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Tips</Label>
                                        <Textarea
                                          value={homeworkData.tips}
                                          onChange={(e) => setHomeworkData(prev => ({ ...prev, tips: e.target.value }))}
                                          rows={2}
                                        />
                                      </div>
                                      <Button onClick={handleCreateHomework} className="btn-primary w-full">
                                        Assign Homework
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </>
                            )}
                            {enrollment.sessions_completed >= 4 && (
                              <Button
                                size="sm"
                                className="bg-olive"
                                onClick={() => handleGraduate(enrollment.id)}
                                data-testid={`graduate-${enrollment.id}`}
                              >
                                <GraduationCap className="w-3 h-3 mr-1" />
                                {enrollment.status === "completed" ? "Certificate" : "Graduate"}
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`delete-enrollment-${enrollment.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Enrollment</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete the enrollment for <strong>{enrollment.puppy_name}</strong>? 
                                    This will also delete all report cards and homework for this enrollment.
                                    This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => handleDeleteEnrollment(enrollment.id, enrollment.puppy_name)}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Manual Enroll Dialog */}
            <Dialog open={manualEnrollOpen} onOpenChange={setManualEnrollOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Manual Enrollment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Select Client</Label>
                    <Select
                      value={manualEnrollData.client_id}
                      onValueChange={handleClientSelect}
                    >
                      <SelectTrigger><SelectValue placeholder="Select a client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} ({client.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Select Puppy</Label>
                    <Select
                      value={manualEnrollData.puppy_id}
                      onValueChange={(v) => setManualEnrollData(prev => ({ ...prev, puppy_id: v }))}
                      disabled={!manualEnrollData.client_id}
                    >
                      <SelectTrigger><SelectValue placeholder={manualEnrollData.client_id ? "Select a puppy" : "Select client first"} /></SelectTrigger>
                      <SelectContent>
                        {selectedClientPuppies.map(puppy => (
                          <SelectItem key={puppy.id} value={puppy.id}>
                            {puppy.name} ({puppy.breed})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {manualEnrollData.client_id && selectedClientPuppies.length === 0 && (
                      <p className="text-xs text-terracotta">This client has no registered puppies</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Select Intake</Label>
                    <Select
                      value={manualEnrollData.intake_id}
                      onValueChange={(v) => setManualEnrollData(prev => ({ ...prev, intake_id: v }))}
                    >
                      <SelectTrigger><SelectValue placeholder="Select an intake" /></SelectTrigger>
                      <SelectContent>
                        {availableIntakes.map(intake => (
                          <SelectItem key={intake.id} value={intake.id}>
                            {intake.program_name} - {intake.start_date} ({intake.day_of_week}) - {intake.available_spots} spots
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={manualEnrollData.notes}
                      onChange={(e) => setManualEnrollData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any notes about this enrollment..."
                      rows={2}
                    />
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Note:</strong> This will create an enrollment with "Pending Payment" status. 
                    You can then send a payment link to the client.
                  </div>

                  <Button 
                    onClick={handleManualEnroll} 
                    className="btn-primary w-full"
                    disabled={!manualEnrollData.puppy_id || !manualEnrollData.intake_id}
                  >
                    Create Enrollment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Intakes Tab */}
          <TabsContent value="intakes">
            <IntakesTab
              intakes={intakes}
              enrollments={enrollments}
              getAuthHeaders={getAuthHeaders}
              fetchAllData={fetchAllData}
            />
          </TabsContent>

          {/* Dogs Tab */}
          <TabsContent value="puppies">
            <DogsTab
              puppies={puppies}
              clients={clients}
              intakes={intakes}
              enrollments={enrollments}
              getAuthHeaders={getAuthHeaders}
              fetchAllData={fetchAllData}
            />
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistTab
              waitlist={waitlist}
              getAuthHeaders={getAuthHeaders}
              fetchAllData={fetchAllData}
            />
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <InvoicesTab clients={clients} />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <MessagesTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Breeder Referrals Tab */}
          <TabsContent value="breeders">
            <BreedersTab
              breederReferrals={breederReferrals}
              getAuthHeaders={getAuthHeaders}
              fetchAllData={fetchAllData}
            />
          </TabsContent>

          {/* Client Videos Tab */}
          <TabsContent value="videos">
            <div className="space-y-6">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <h2 className="font-heading text-xl text-charcoal">Client Training Videos</h2>
                <div className="flex gap-2 items-center flex-wrap">
                  <Button 
                    size="sm" 
                    variant={showArchivedVideos ? "default" : "outline"}
                    className={showArchivedVideos ? "bg-olive" : ""}
                    onClick={() => setShowArchivedVideos(!showArchivedVideos)}
                    data-testid="toggle-archived-videos"
                  >
                    <Archive className="w-3 h-3 mr-1" />
                    {showArchivedVideos ? "Hide Archived" : `Show Archived (${archivedVideos.length})`}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    onClick={async () => {
                      if (window.confirm("This will delete all broken video entries (where the actual file doesn't exist). Continue?")) {
                        try {
                          const res = await axios.delete(`${API}/client-videos/cleanup/broken`, { headers: getAuthHeaders() });
                          toast.success(res.data.message);
                          fetchAllData();
                        } catch (err) {
                          toast.error("Failed to cleanup videos");
                        }
                      }
                    }}
                    data-testid="cleanup-broken-videos"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clean Up Broken
                  </Button>
                </div>
              </div>
              
              {/* Storage Stats Card */}
              {videoStorageStats && (
                <div className="bg-sand/30 rounded-xl p-4 mb-6">
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="flex flex-wrap gap-6">
                      <div>
                        <p className="text-stone text-xs uppercase tracking-wide">Total Videos</p>
                        <p className="font-heading text-2xl text-charcoal">{videoStorageStats.total_videos}</p>
                      </div>
                      <div>
                        <p className="text-stone text-xs uppercase tracking-wide">Active</p>
                        <p className="font-heading text-2xl text-olive">{videoStorageStats.active_videos}</p>
                      </div>
                      <div>
                        <p className="text-stone text-xs uppercase tracking-wide">Archived</p>
                        <p className="font-heading text-2xl text-stone">{videoStorageStats.archived_videos}</p>
                      </div>
                      <div>
                        <p className="text-stone text-xs uppercase tracking-wide">Est. Storage</p>
                        <p className="font-heading text-xl text-charcoal">{videoStorageStats.storage?.total}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Select
                        onValueChange={async (months) => {
                          if (window.confirm(`Archive all videos older than ${months} months? They will still be accessible in the Archived section.`)) {
                            try {
                              const res = await axios.put(`${API}/client-videos/bulk-archive?months_old=${months}`, {}, { headers: getAuthHeaders() });
                              toast.success(res.data.message);
                              fetchAllData();
                            } catch (err) {
                              toast.error("Failed to bulk archive");
                            }
                          }
                        }}
                      >
                        <SelectTrigger className="w-[180px]" data-testid="bulk-archive-select">
                          <SelectValue placeholder="Bulk Archive..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">Archive 3+ months old</SelectItem>
                          <SelectItem value="6">Archive 6+ months old</SelectItem>
                          <SelectItem value="12">Archive 12+ months old</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Videos organized by client */}
              {activeEnrollments.length === 0 ? (
                <div className="card-premium text-center py-12">
                  <Video className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-charcoal mb-2">No Active Enrollments</h3>
                  <p className="text-stone">Videos can be uploaded for active enrollments from the Enrollments tab.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {activeEnrollments.map(enrollment => {
                      const enrollmentVideos = videos.filter(v => v.enrollment_id === enrollment.id);
                      return (
                        <div key={enrollment.id} className="card-premium">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-heading text-lg text-charcoal">{enrollment.puppy_name}</h3>
                              <p className="text-stone text-sm">{enrollment.owner_name} • {enrollment.program_name}</p>
                            </div>
                            <Button 
                              size="sm" 
                              className="btn-primary"
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setClientVideoData({
                                  enrollment_id: enrollment.id,
                                  title: "",
                                  description: "",
                                  week_number: Math.min(enrollment.sessions_completed + 1, 4)
                                });
                                setClientVideoOpen(true);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Upload Video
                            </Button>
                            {enrollmentVideos.length > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleArchiveEnrollmentVideos(enrollment.id, enrollment.puppy_name)}
                                data-testid={`archive-all-${enrollment.id}`}
                              >
                                <Archive className="w-3 h-3 mr-1" /> Archive All
                              </Button>
                            )}
                          </div>
                          
                          {(() => {
                            const archivedForEnrollment = archivedVideos.filter(v => v.enrollment_id === enrollment.id);
                            if (enrollmentVideos.length === 0 && archivedForEnrollment.length > 0) {
                              return (
                                <div className="bg-stone/10 rounded-lg p-4 flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-stone">
                                    <Archive className="w-4 h-4" />
                                    <span className="text-sm italic">
                                      {archivedForEnrollment.length} video{archivedForEnrollment.length !== 1 ? 's' : ''} archived
                                    </span>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-olive"
                                    onClick={() => setShowArchivedVideos(true)}
                                  >
                                    View in Archived
                                  </Button>
                                </div>
                              );
                            } else if (enrollmentVideos.length === 0) {
                              return <p className="text-stone text-sm italic">No videos uploaded yet</p>;
                            }
                            return (
                              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {enrollmentVideos.map(video => (
                                <div key={video.id} className="bg-sand/30 rounded-lg p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <Badge className="bg-olive/10 text-olive">Week {video.week_number}</Badge>
                                    <div className="flex gap-1">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-6 w-6 p-0 text-stone hover:text-olive"
                                        onClick={() => handleArchiveVideo(video.id, true)}
                                        title="Archive video"
                                      >
                                        <Archive className="w-3 h-3" />
                                      </Button>
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500">
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Video</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Are you sure you want to delete "{video.title}"?
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction 
                                              className="bg-red-600 hover:bg-red-700"
                                              onClick={() => handleDeleteClientVideo(video.id)}
                                            >
                                              Delete
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  </div>
                                  <h4 className="font-medium text-charcoal mb-1">{video.title}</h4>
                                  {video.description && <p className="text-stone text-xs mb-2">{video.description}</p>}
                                  <a 
                                    href={video.stream_url || `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-olive text-sm hover:underline inline-flex items-center gap-1"
                                  >
                                    <Video className="w-3 h-3" /> Watch Video
                                  </a>
                                </div>
                              ))}
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Archived Videos Section */}
              {showArchivedVideos && archivedVideos.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-heading text-lg text-charcoal mb-4 flex items-center gap-2">
                    <Archive className="w-5 h-5 text-stone" />
                    Archived Videos ({archivedVideos.length})
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {archivedVideos.map(video => {
                      const enrollment = enrollments.find(e => e.id === video.enrollment_id);
                      return (
                        <div key={video.id} className="bg-stone/10 rounded-lg p-4 opacity-80">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-stone/20 text-stone text-xs w-fit">
                                {enrollment?.puppy_name || "Unknown"}
                              </Badge>
                              <Badge className="bg-olive/10 text-olive text-xs w-fit">Week {video.week_number}</Badge>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-olive hover:text-olive/80"
                              onClick={() => handleArchiveVideo(video.id, false)}
                              title="Restore video"
                            >
                              <ArchiveRestore className="w-3 h-3" />
                            </Button>
                          </div>
                          <h4 className="font-medium text-charcoal mb-1 text-sm">{video.title}</h4>
                          <p className="text-stone text-xs mb-2">{enrollment?.owner_name}</p>
                          <a 
                            href={video.stream_url || `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-olive text-sm hover:underline inline-flex items-center gap-1"
                          >
                            <Video className="w-3 h-3" /> Watch
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Standalone Video Upload Dialog for Videos Tab */}
              <Dialog open={clientVideoOpen && activeTab === "videos"} onOpenChange={(open) => {
                setClientVideoOpen(open);
                if (!open) {
                  setClientVideoData({ enrollment_id: "", title: "", description: "", week_number: 1 });
                }
              }}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Training Video</DialogTitle>
                    <DialogDescription>
                      {selectedEnrollment && `Upload a video for ${selectedEnrollment.puppy_name}`}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Video Title</Label>
                      <Input 
                        value={clientVideoData.title}
                        onChange={(e) => setClientVideoData({...clientVideoData, title: e.target.value})}
                        placeholder="e.g., Week 1 Training Session"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Week Number</Label>
                      <Select 
                        value={clientVideoData.week_number.toString()} 
                        onValueChange={(v) => setClientVideoData({...clientVideoData, week_number: parseInt(v)})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4].map(w => (
                            <SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Textarea 
                        value={clientVideoData.description}
                        onChange={(e) => setClientVideoData({...clientVideoData, description: e.target.value})}
                        placeholder="Add notes about this training video..."
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Video File</Label>
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={handleUploadClientVideo}
                        disabled={uploadingVideo}
                        className="block w-full text-sm text-stone file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-olive file:text-white hover:file:bg-olive/90 disabled:opacity-50"
                      />
                      <p className="text-xs text-stone">Supports large files up to 1GB. MP4, MOV, AVI, WebM</p>
                    </div>
                    {uploadingVideo && (
                      <div className="py-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-stone text-sm">
                            {videoUploadProgress < 100 ? "Uploading video..." : "Processing video..."}
                          </span>
                          <span className="text-olive font-medium">{videoUploadProgress}%</span>
                        </div>
                        <div className="w-full bg-sand rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-300 ${videoUploadProgress >= 100 ? 'bg-terracotta animate-pulse' : 'bg-olive'}`}
                            style={{ width: `${videoUploadProgress}%` }}
                          />
                        </div>
                        {videoUploadProgress < 100 ? (
                          <p className="text-xs text-stone mt-2">Please don't close this window while uploading...</p>
                        ) : (
                          <p className="text-xs text-terracotta mt-2 font-medium">
                            ⏳ Processing large file... This may take a few minutes. Check the toast for status.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <ResourcesTab
              trainingResources={trainingResources}
              emailTemplates={emailTemplates}
              clients={clients}
              getAuthHeaders={getAuthHeaders}
              fetchAllData={fetchAllData}
            />
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <div className="card-premium p-0 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-sand/50">
                    <TableHead>Name</TableHead>
                    <TableHead>Dogs</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-stone">
                        No clients yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => {
                      const clientPuppies = puppies.filter(p => p.user_id === client.id || p.owner_id === client.id);
                      return (
                        <TableRow key={client.id}>
                          <TableCell 
                            className="font-medium cursor-pointer hover:text-olive transition-colors"
                            onClick={() => {
                              setSelectedClientDetail(client);
                              setClientDetailOpen(true);
                            }}
                          >
                            {client.name}
                          </TableCell>
                          <TableCell>
                            {clientPuppies.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {clientPuppies.map(p => (
                                  <span key={p.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-olive/10 text-olive">
                                    🐕 {p.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-stone text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>{client.email}</TableCell>
                          <TableCell>{client.phone}</TableCell>
                          <TableCell>{new Date(client.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Dialog open={messageOpen && selectedRecipient?.id === client.id} onOpenChange={(open) => {
                                setMessageOpen(open);
                                if (open) setSelectedRecipient(client);
                              }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline" data-testid={`message-${client.id}`}>
                                    <Send className="w-3 h-3 mr-1" />
                                    Message
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Message {client.name}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <Textarea
                                      value={messageContent}
                                      onChange={(e) => setMessageContent(e.target.value)}
                                      placeholder="Type your message..."
                                      rows={4}
                                    />
                                    <Button onClick={handleSendMessage} className="btn-primary w-full">
                                      Send Message
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`delete-client-${client.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Client</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete <strong>{client.name}</strong>? 
                                      <span className="block mt-2 text-red-600">
                                        Warning: This will permanently delete all of their data including puppies, enrollments, report cards, homework, and graduation records.
                                      </span>
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => handleDeleteClient(client.id, client.name)}
                                  >
                                    Delete Client
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Client Detail Dialog */}
            <Dialog open={clientDetailOpen} onOpenChange={setClientDetailOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="flex flex-row items-center justify-between">
                  <DialogTitle className="font-heading text-2xl">{selectedClientDetail?.name}</DialogTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePrint('client-print-content', `${selectedClientDetail?.name} - Client Profile`)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                </DialogHeader>
                {selectedClientDetail && (
                  <div id="client-print-content" className="space-y-6 mt-4">
                    {/* Contact Info */}
                    <div className="bg-sand/50 rounded-lg p-4 space-y-3">
                      <h4 className="font-medium text-charcoal">Contact Information</h4>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-olive" />
                        <span className="text-charcoal">{selectedClientDetail.email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-olive" />
                        <span className="text-charcoal">{selectedClientDetail.phone || "Not provided"}</span>
                      </div>
                      <p className="text-stone text-sm">
                        Member since {new Date(selectedClientDetail.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Client's Puppies */}
                    <div>
                      <h4 className="font-medium text-charcoal mb-3">Dogs ({getClientPuppies(selectedClientDetail.id).length})</h4>
                      {getClientPuppies(selectedClientDetail.id).length === 0 ? (
                        <p className="text-stone text-sm">No dogs registered</p>
                      ) : (
                        <div className="grid gap-3">
                          {getClientPuppies(selectedClientDetail.id).map(puppy => (
                            <div key={puppy.id} className="bg-white p-4 rounded-lg border border-olive/10 flex items-center gap-4">
                              <div className="w-16 h-16 rounded-lg overflow-hidden bg-sand/50 flex-shrink-0">
                                {puppy.photos?.length > 0 ? (
                                  <img 
                                    src={`${process.env.REACT_APP_BACKEND_URL}${puppy.photos[0]}`}
                                    alt={puppy.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Dog className="w-8 h-8 text-olive/30" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <h5 className="font-medium text-charcoal">{puppy.name}</h5>
                                <p className="text-stone text-sm">{puppy.breed} • {puppy.age_weeks} weeks</p>
                                <p className="text-stone text-xs">{puppy.pickup_address}</p>
                              </div>
                              <Badge className={
                                puppy.vaccination_status === "up_to_date" ? "bg-olive" :
                                puppy.vaccination_status === "partial" ? "bg-terracotta" : "bg-sand text-charcoal"
                              }>
                                {puppy.vaccination_status?.replace(/_/g, " ")}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Client's Enrollments */}
                    <div>
                      <h4 className="font-medium text-charcoal mb-3">Enrollments ({getClientEnrollments(selectedClientDetail.id).length})</h4>
                      {getClientEnrollments(selectedClientDetail.id).length === 0 ? (
                        <p className="text-stone text-sm">No enrollments yet</p>
                      ) : (
                        <div className="space-y-3">
                          {getClientEnrollments(selectedClientDetail.id).map(enrollment => {
                            const puppy = puppies.find(p => p.id === enrollment.puppy_id);
                            return (
                              <div key={enrollment.id} className="bg-white p-4 rounded-lg border border-olive/10">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <h5 className="font-medium text-charcoal">{enrollment.program_name}</h5>
                                    <p className="text-stone text-sm">Dog: {puppy?.name || "Unknown"}</p>
                                  </div>
                                  <Badge className={
                                    enrollment.status === "confirmed" || enrollment.status === "in_progress" ? "bg-olive" :
                                    enrollment.status === "completed" ? "bg-charcoal" :
                                    enrollment.status === "pending" ? "bg-terracotta" : "bg-sand text-charcoal"
                                  }>
                                    {enrollment.status}
                                  </Badge>
                                </div>
                                <div className="text-sm text-stone">
                                  <span>Start: {enrollment.start_date}</span>
                                  <span className="mx-2">•</span>
                                  <span>{enrollment.day_of_week}s</span>
                                  <span className="mx-2">•</span>
                                  <span>{enrollment.sessions_completed || 0}/4 sessions</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="pt-4 border-t border-olive/10">
                      <h4 className="font-medium text-charcoal mb-3">Quick Actions</h4>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setClientDetailOpen(false);
                            setActiveTab("invoices");
                          }}
                          data-testid="send-invoice-btn-client"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Send Invoice
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setClientDetailOpen(false);
                            setSelectedRecipient({
                              id: selectedClientDetail.id,
                              name: selectedClientDetail.name
                            });
                            setMessageOpen(true);
                          }}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Send Message
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* Certificate Editor Dialog */}
        <CertificateEditorDialog
          open={certificateOpen}
          onOpenChange={setCertificateOpen}
          enrollment={selectedCertificateEnrollment}
          getAuthHeaders={getAuthHeaders}
          onCertificateCreated={handleCertificateCreated}
        />
      </main>
    </div>
  );
};

export default TrainerDashboard;
